/* ---------------------------------------------------------------------------
 * ZAHNHELD PLP BACKDROP
 *
 * Drives a highlights rail (Zahnheld PLP Karten, placement "Behind the buy box")
 * that the product section has docked into its left column:
 *
 *   1. Marks the product grid with .product--zh-backdrop, which switches on the
 *      layout in zh-plp.css: pinned stage on the left, white cover and buy box
 *      on the right, rail screen wide underneath both.
 *   2. Measures how far the rail can scroll and how tall the buy box is, and
 *      makes the grid tall enough that the stage stays pinned for exactly that
 *      distance, and long enough for a buy box taller than the stage to scroll
 *      fully into view. Pinning the buy box itself is Impact's <safe-sticky>,
 *      which already handles a box taller than the viewport.
 *   3. Turns the page scroll through the grid into rail.scrollLeft, so the cards
 *      come out from under the buy box as the visitor scrolls down.
 *   4. Redirects the rail's own arrows to the page scroll, otherwise they would
 *      move the rail and the next scroll frame would move it straight back.
 *
 * Everything switches off below 1000px (where the product section sends the rail
 * home anyway) and under prefers-reduced-motion, where the rail stays an ordinary
 * horizontal scroller with working arrows. The measuring maths is exposed on
 * window.__zhPlpBackdrop for tests/zh-plp-backdrop.test.js.
 * ------------------------------------------------------------------------- */
(function () {
  'use strict';

  /* The section renders one script tag per backdrop rail. A second rail on the page
     would run this file twice and register every window listener twice. */
  if (window.__zhPlpBackdrop) return;

  var DESKTOP_MQ = '(min-width: 1000px)';
  var REDUCE_MQ = '(prefers-reduced-motion: reduce)';
  var SECTION = '[data-zh-plp-backdrop]';
  var RAIL = '[data-zh-plp-rail]';
  var ACTIVE_CLASS = 'product--zh-backdrop';
  /* White ground left of the buy box column, from the cover's edge to the column. */
  var GAP = 72;
  /* The picture cell is at least square and at most this much taller than wide. */
  var MEDIA_MAX_RATIO = 1.5;
  /* Air the buy box keeps to the viewport edge while pinned. Same 20px Impact's
     safe-sticky uses, so the grid is long enough for its last block to show. */
  var INFO_MARGIN = 20;

  /* -------------------------------------------------------------------------
   * Pure maths, all in px. Kept free of the DOM so they can be tested.
   * ----------------------------------------------------------------------- */

  function clamp01(value) {
    if (!(value > 0)) return 0;
    return value > 1 ? 1 : value;
  }

  /* 0 at the scroll position where the stage pins, 1 where the grid lets go. */
  function progress(scrollY, start, travel) {
    if (!(travel > 0)) return 1;
    return clamp01((scrollY - start) / travel);
  }

  /* How much taller than one stage the grid has to be. At least the rail's own
     overflow, and at least enough for a tall buy box to slide fully into view. */
  function travelFor(railTravel, infoHeight, stageHeight) {
    var forInfo = infoHeight + INFO_MARGIN - stageHeight;
    return Math.max(railTravel || 0, forInfo, 0);
  }

  /* Width of the white cover: from its edge, a gap left of the buy box column, to the
     right edge of the viewport. */
  function coverWidth(viewportWidth, rightColLeft, gap) {
    return Math.max(0, viewportWidth - rightColLeft + gap);
  }

  /* Where the cover's edge sits, measured from the stage's left edge. */
  function coverX(rightColLeft, leftColLeft, gap) {
    return Math.max(0, rightColLeft - gap - leftColLeft);
  }

  /* Height of the pinned stage: the buy box's own height, but never taller than the
     viewport under the sticky header (the stage has to be visible whole while pinned)
     and never shorter than the rail block with a square picture. */
  function stageHeightFor(infoHeight, viewportStage, railMinimum) {
    return Math.round(Math.max(railMinimum, Math.min(infoHeight, viewportStage)));
  }

  /* Height of the card's picture cell that makes the rail block (head plus card) as
     tall as the buy box. `chrome` is everything in the block that is not the picture:
     head, its margin and the card's text body. Never shorter than square, never taller
     than the stage allows or than MEDIA_MAX_RATIO. */
  function mediaHeightFor(cardWidth, chrome, targetHeight, stageHeight) {
    var wanted = targetHeight - chrome;
    var fits = stageHeight - chrome;
    var max = cardWidth * MEDIA_MAX_RATIO;
    return Math.round(Math.max(cardWidth, Math.min(max, wanted, fits)));
  }

  /* Page scroll that moves the rail by one card. The rail travel is mapped onto the
     grid travel, so a card step is scaled by the same ratio. */
  function arrowDelta(direction, cardStep, railTravel, travel) {
    if (!(railTravel > 0) || !(cardStep > 0)) return 0;
    return direction * cardStep * (travel / railTravel);
  }

  /* -------------------------------------------------------------------------
   * DOM
   * ----------------------------------------------------------------------- */

  var desktop = window.matchMedia(DESKTOP_MQ);
  var reduce = window.matchMedia(REDUCE_MQ);
  var states = [];
  var frame = null;

  function wanted() {
    return desktop.matches && !reduce.matches;
  }

  function railOf(band) {
    return band.querySelector(RAIL);
  }

  /* One card plus the column gap, read from the DOM like zh-plp.js does. */
  function cardStep(rail) {
    var card = rail.querySelector('.zh-plp-card');
    if (!card) return rail.clientWidth;
    var gap = parseFloat(getComputedStyle(rail).columnGap) || 0;
    return card.getBoundingClientRect().width + gap;
  }

  function setVar(el, name, value) {
    el.style.setProperty(name, value);
  }

  function clearVars(product) {
    ['--zh-bd-travel', '--zh-bd-cover', '--zh-bd-cover-x', '--zh-bd-stage-h', '--zh-bd-media-h', '--zh-bd-info-offset', '--zh-bd-title-fs', '--zh-bd-title-lh'].forEach(function (name) {
      product.style.removeProperty(name);
    });
  }

  function deactivate(state) {
    if (state.product) {
      state.product.classList.remove(ACTIVE_CLASS);
      clearVars(state.product);
    }
    state.active = false;
    state.product = null;
  }

  /* The elements a measurement needs, or null while the band is not docked into a
     product column on a desktop screen. */
  function resolve(state) {
    var band = state.band;
    var product = band.closest('.product');
    if (!wanted() || !product || !band.closest('.product__dock--left')) return null;
    var parts = {
      product: product,
      leftCol: product.querySelector('.product__col--left'),
      rightCol: product.querySelector('.product__col--right'),
      info: product.querySelector('.product-info'),
      rail: railOf(band)
    };
    if (!parts.leftCol || !parts.rightCol || !parts.info || !parts.rail) return null;
    return parts;
  }

  /* The cover first: the rail's trailing spacer takes its width, and the rail's
     scrollable overflow is only right once that spacer has its size. */
  function applyCover(parts) {
    var viewportWidth = document.documentElement.clientWidth;
    var rightColLeft = parts.rightCol.getBoundingClientRect().left;
    setVar(parts.product, '--zh-bd-cover', coverWidth(viewportWidth, rightColLeft, GAP) + 'px');
    setVar(parts.product, '--zh-bd-cover-x', coverX(rightColLeft, parts.leftCol.getBoundingClientRect().left, GAP) + 'px');
  }

  /* The rail heading borrows the buy box title's metrics. */
  function applyTitle(parts) {
    var title = parts.info.querySelector('.product-info__title');
    if (!title) return;
    var titleStyle = getComputedStyle(title);
    setVar(parts.product, '--zh-bd-title-fs', titleStyle.fontSize);
    setVar(parts.product, '--zh-bd-title-lh', titleStyle.lineHeight);
  }

  /* Stage and picture cell, so the rail block ends level with the buy box. Every cell
     has the same height and the tallest card sets the rail's, so the block minus one
     cell is exactly the chrome: head, its margin, the tallest text body, borders.
     Returns the stage height it settled on. */
  function applyStage(parts, band, infoHeight, viewportStage) {
    var card = parts.rail.querySelector('.zh-plp-card');
    var media = card && card.querySelector('.zh-plp-card__media');
    if (!card || !media) return viewportStage;
    var cardWidth = card.getBoundingClientRect().width;
    var chrome = band.getBoundingClientRect().height - media.getBoundingClientRect().height;
    var stageHeight = stageHeightFor(infoHeight, viewportStage, chrome + cardWidth);
    setVar(parts.product, '--zh-bd-stage-h', stageHeight + 'px');
    setVar(parts.product, '--zh-bd-media-h', mediaHeightFor(cardWidth, chrome, infoHeight, stageHeight) + 'px');
    return stageHeight;
  }

  /* Reads the geometry and writes the custom properties. Called on init, on load,
     on resize, whenever the docking script moves the section and whenever the buy
     box changes height. */
  function measure(state) {
    var parts = resolve(state);
    if (!parts) {
      deactivate(state);
      return;
    }

    state.product = parts.product;
    state.rail = parts.rail;
    parts.product.classList.add(ACTIVE_CLASS);

    applyCover(parts);
    applyTitle(parts);

    /* Resolved px of the stage's sticky offset. `top` is a real property, so the
       computed value comes back in px, unlike the custom property behind it. */
    var stickyOffset = parseFloat(getComputedStyle(parts.leftCol).top) || 0;
    var infoHeight = parts.info.getBoundingClientRect().height;
    var stageHeight = applyStage(parts, state.band, infoHeight, window.innerHeight - stickyOffset);

    /* Half of what the stage has left over under a buy box shorter than the rail block. A
       stylesheet can shift the box by it (as a transform, which leaves the sticky offset and
       this measurement alone) to centre it against the rail; zh-plp.css does not use it. */
    setVar(parts.product, '--zh-bd-info-offset', Math.max(0, Math.round((stageHeight - infoHeight) / 2)) + 'px');

    var railTravel = Math.max(0, parts.rail.scrollWidth - parts.rail.clientWidth);
    var travel = travelFor(railTravel, infoHeight, stageHeight);
    setVar(parts.product, '--zh-bd-travel', travel + 'px');

    state.railTravel = railTravel;
    state.travel = travel;
    state.stickyOffset = stickyOffset;
    state.active = true;
    apply(state);
  }

  /* The start is read every frame rather than cached: a banner or an app block that
     renders late above the product section moves the grid without a resize, and a
     cached start would leave the rail running out of step with the page. */
  function apply(state) {
    if (!state.active) return;
    var start = state.product.getBoundingClientRect().top + window.scrollY - state.stickyOffset;
    var target = Math.round(progress(window.scrollY, start, state.travel) * state.railTravel);
    if (Math.abs(state.rail.scrollLeft - target) >= 1) state.rail.scrollLeft = target;
  }

  function onScroll() {
    if (frame) return;
    frame = window.requestAnimationFrame(function () {
      frame = null;
      states.forEach(apply);
    });
  }

  function measureAll() {
    states.forEach(measure);
  }

  var resizeTimer = null;
  function onResize() {
    if (resizeTimer) clearTimeout(resizeTimer);
    resizeTimer = setTimeout(measureAll, 120);
  }

  /* A band the theme editor replaced is gone for good: its observers stop and its
     state leaves the list, so nothing keeps measuring a detached node. */
  function dispose(state) {
    state.observers.forEach(function (observer) {
      observer.disconnect();
    });
    state.observers = [];
    deactivate(state);
    states = states.filter(function (other) {
      return other !== state;
    });
  }

  function onChange(state) {
    if (!state.band.isConnected) {
      dispose(state);
      return;
    }
    measure(state);
  }

  function watch(state) {
    /* The docking script moves the whole section node, so the band cannot observe
       its own move. Watching the dock's child list catches it arriving, and every
       later re-balance. The dock exists at DOMContentLoaded, it is server rendered. */
    var dock = document.querySelector('.product__dock--left');
    if (dock && typeof window.MutationObserver === 'function') {
      var dockObserver = new MutationObserver(function () {
        onChange(state);
      });
      dockObserver.observe(dock, { childList: true });
      state.observers.push(dockObserver);
    }

    /* The buy box grows when the variant picker, the upsell group or the delivery
       estimate render, and each time the pin point moves. */
    var info = document.querySelector('.product .product-info');
    if (info && typeof window.ResizeObserver === 'function') {
      var infoObserver = new ResizeObserver(function () {
        onChange(state);
      });
      infoObserver.observe(info);
      state.observers.push(infoObserver);
    }

    state.band.dataset.zhBackdropReady = '1';
  }

  function init(root) {
    states.slice().forEach(function (state) {
      if (!state.band.isConnected) dispose(state);
    });
    var bands = (root || document).querySelectorAll(SECTION);
    Array.prototype.forEach.call(bands, function (band) {
      if (band.dataset.zhBackdropReady) return;
      var state = { band: band, active: false, product: null, rail: null, observers: [], stickyOffset: 0, travel: 0, railTravel: 0 };
      states.push(state);
      watch(state);
      measure(state);
    });
  }

  /* Arrows: capture phase, so zh-plp.js never sees the click and does not scroll
     the rail on its own. */
  document.addEventListener(
    'click',
    function (event) {
      var arrow = event.target.closest('[data-zh-plp-rail-prev], [data-zh-plp-rail-next]');
      if (!arrow) return;
      var band = arrow.closest(SECTION);
      if (!band) return;
      var state = null;
      for (var i = 0; i < states.length; i += 1) {
        if (states[i].band === band) state = states[i];
      }
      if (!state || !state.active) return;

      event.stopPropagation();
      var direction = arrow.hasAttribute('data-zh-plp-rail-prev') ? -1 : 1;
      var delta = arrowDelta(direction, cardStep(state.rail), state.railTravel, state.travel);
      if (delta) window.scrollBy({ top: delta, behavior: 'smooth' });
    },
    true
  );

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onResize);
  window.addEventListener('load', measureAll);

  function onMqChange() {
    measureAll();
  }
  if (typeof desktop.addEventListener === 'function') {
    desktop.addEventListener('change', onMqChange);
    reduce.addEventListener('change', onMqChange);
  } else if (typeof desktop.addListener === 'function') {
    desktop.addListener(onMqChange);
    reduce.addListener(onMqChange);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  /* Theme editor: a re-rendered rail section is a new node, a re-rendered product
     section has new columns. Either way the docking script runs again, and the
     fresh node registers itself here. */
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
    setTimeout(measureAll, 0);
  });

  window.__zhPlpBackdrop = {
    progress: progress,
    travelFor: travelFor,
    coverWidth: coverWidth,
    coverX: coverX,
    stageHeightFor: stageHeightFor,
    mediaHeightFor: mediaHeightFor,
    arrowDelta: arrowDelta,
    MEDIA_MAX_RATIO: MEDIA_MAX_RATIO,
    INFO_MARGIN: INFO_MARGIN,
    GAP: GAP
  };
})();
